import axios, { AxiosError } from 'axios';

// Single Axios instance for the whole app. baseURL defaults to the relative "/api"
// (Vite proxies it in dev, nginx in prod). Override with VITE_API_BASE_URL.
const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export const api = axios.create({ baseURL });

// --- Token storage (sessionStorage; no refresh token in MVP — architecture §4.3) ---
const TOKEN_KEY = 'scc.token';
let identityGeneration = 0;

export interface IdentitySnapshot {
  readonly token: string | null;
  readonly generation: number;
}

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    identitySnapshot?: IdentitySnapshot;
  }
}

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string): void {
  identityGeneration++;
  sessionStorage.setItem(TOKEN_KEY, token);
}
export function clearToken(): void {
  identityGeneration++;
  sessionStorage.removeItem(TOKEN_KEY);
}

export function captureIdentity(): IdentitySnapshot {
  return { token: getToken(), generation: identityGeneration };
}

export function isCurrentIdentity(snapshot: IdentitySnapshot | undefined): boolean {
  return !!snapshot?.token && snapshot.generation === identityGeneration && snapshot.token === getToken();
}

// --- Global 401/403 handlers, registered by AuthContext (interceptors live outside React) ---
type Handler = () => void;
let onUnauthorized: Handler | null = null;
let onForbidden: Handler | null = null;

export function setUnauthorizedHandler(fn: Handler | null): void {
  onUnauthorized = fn;
}
export function setForbiddenHandler(fn: Handler | null): void {
  onForbidden = fn;
}

// Request interceptor: attach the bearer token to every /api call.
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    config.identitySnapshot = captureIdentity();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  undefined,
  // Capture the session before logout clears local state, rather than in a later microtask.
  { synchronous: true },
);

// Response interceptor: 401 → clear session + redirect to /login; 403 → access-denied page.
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    // A response belongs to the identity that sent it, not whichever user is signed in now.
    if (isCurrentIdentity(error.config?.identitySnapshot) && status === 401) {
      clearToken();
      onUnauthorized?.();
    } else if (isCurrentIdentity(error.config?.identitySnapshot) && status === 403) {
      onForbidden?.();
    }
    return Promise.reject(error);
  },
);

/** Backend error shape from ExceptionHandlingMiddleware: { code, message }. */
export interface ApiError {
  code: string;
  message: string;
  status?: number;
}

/** Normalize any thrown value into an ApiError for display. */
export function toApiError(err: unknown): ApiError {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Partial<ApiError> | undefined;
    return {
      code: data?.code ?? 'NETWORK_ERROR',
      message: data?.message ?? err.message ?? 'خطای ارتباط با سرور',
      status: err.response?.status,
    };
  }
  return { code: 'UNKNOWN', message: 'خطای ناشناخته' };
}
